import type { Item } from '../db/types';

export interface Heading {
  /** 1-based line number, so the outline can jump the editor straight to it. */
  line: number;
  level: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const FENCE = /^\s*```/;

/**
 * Headings in document order.
 *
 * Lines inside fenced code blocks are skipped — a `# comment` in a shell
 * snippet is not a section of the note, and letting it into the outline makes
 * the panel useless in exactly the notes that need it most.
 */
export function outlineOf(body: string): Heading[] {
  const out: Heading[] = [];
  let inFence = false;

  body.split('\n').forEach((raw, i) => {
    if (FENCE.test(raw)) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;

    const match = HEADING.exec(raw);
    if (!match) return;
    out.push({
      line: i + 1,
      level: match[1]!.length as Heading['level'],
      text: match[2]!.trim(),
    });
  });

  return out;
}

export interface DocStats {
  words: number;
  characters: number;
}

/** Counts prose, not syntax — the markers are not words you wrote. */
export function statsOf(body: string): DocStats {
  const prose = body
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*+]\s+(\[[ xX]]\s+)?/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/!?\[([^\]]*)]\([^)]*\)/g, '$1')
    .replace(/[*_~`]/g, '');

  const words = prose.trim() ? prose.trim().split(/\s+/).length : 0;
  return { words, characters: body.length };
}

/**
 * Other items that mention this one by title.
 *
 * There is no wiki-link syntax in the format, and inventing one would put a
 * SlippingNotes-only construct into files whose whole point is being portable
 * markdown. A plain title mention is the honest signal available.
 */
export function backlinksTo(item: Item, all: Item[]): Item[] {
  const title = item.title.trim();
  if (title.length < 3) return [];
  const needle = title.toLowerCase();

  return all.filter(
    (other) =>
      other.id !== item.id &&
      !other.deleted &&
      (other.body.toLowerCase().includes(needle) ||
        other.title.toLowerCase().includes(needle)),
  );
}
